/**
 * localStorage persistence for clip-creator projects.
 *
 * Same reasoning that put the old per-video clip markers in the browser
 * (see ``docs/tools/clip-creator.md``): single-user tool, tiny payload,
 * and a backend sidecar would buy validation + versioning + concurrent
 * edit races that nothing here needs. The *media* still lives on the
 * server; a project is just names, ids and numbers pointing at it.
 *
 * Every read is defensive — a half-written or hand-edited entry is
 * treated as absent rather than crashing the page.
 */

import { loadStoredClips, type ImportedItem } from '@/api/endpoints/videoCutter'
import {
  DEFAULT_GRADE,
  DEFAULT_TEXT,
  DEFAULT_TRANSITION,
  GRADE_LIMITS,
  MAX_FREEZE_S,
  MAX_SPEED,
  MIN_CROP,
  MIN_FREEZE_S,
  MIN_SPEED,
  newId,
  stemOf,
  TRANSITION_OPTIONS,
  type ColorGrade,
  type CropRect,
  type FreezeFrame,
  type ProjectClip,
  type ProjectVideo,
  type TextOverlay,
  type TextTransition,
  type ToolProject,
  type TransitionKind,
} from './model'

const PROJECTS_KEY = 'prism:clip-creator:projects:v1'
/** Set once the pre-project clip markers have been adopted. Its presence
 * is what stops the migration from running (and duplicating projects) on
 * every mount. */
const MIGRATED_KEY = 'prism:clip-creator:legacy-migrated:v1'
/** Player volume. Persisted because the editor is a place you reload a
 * lot — now that ``?project=`` brings you back mid-edit, having the audio
 * jump back to full every time would be its own small papercut. */
const VOLUME_KEY = 'prism:clip-creator:volume:v1'

function readStorage(key: string): string | null {
  if (typeof window === 'undefined') return null
  try {
    return window.localStorage.getItem(key)
  } catch {
    // Private mode / disabled storage — behave as "nothing saved".
    return null
  }
}

function writeStorage(key: string, value: string): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(key, value)
  } catch {
    // Quota or private mode. The projects stay live in React state; they
    // just won't survive a reload. Not worth interrupting the user for.
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function parseVideo(raw: unknown): ProjectVideo | null {
  if (!isRecord(raw)) return null
  const { id, tempPath, videoUrl, proxyUrl, filename, origin, duration, width, height, fileSize } =
    raw
  if (typeof id !== 'string' || typeof tempPath !== 'string' || typeof videoUrl !== 'string') {
    return null
  }
  return {
    id,
    tempPath,
    videoUrl,
    // Absent on projects saved before proxies existed — the player falls
    // back to the master rather than treating the video as unreadable.
    proxyUrl: typeof proxyUrl === 'string' ? proxyUrl : null,
    filename: typeof filename === 'string' ? filename : 'video.mp4',
    origin: origin === 'youtube' ? 'youtube' : 'upload',
    duration: typeof duration === 'number' ? duration : 0,
    width: typeof width === 'number' ? width : null,
    height: typeof height === 'number' ? height : null,
    fileSize: typeof fileSize === 'number' ? fileSize : null,
  }
}

/** A finite number inside ``[min, max]``, or the fallback. Shared by every
 * bounded field below — a value out of range is a corrupted entry, and
 * clamping it silently would hide the corruption rather than recover from
 * it. */
function bounded(value: unknown, min: number, max: number, fallback: number): number {
  return typeof value === 'number' && isFinite(value) && value >= min && value <= max
    ? value
    : fallback
}

function parseFreeze(raw: unknown): FreezeFrame | null {
  if (!isRecord(raw)) return null
  const { id, at, duration } = raw
  if (typeof id !== 'string' || typeof at !== 'number' || !isFinite(at) || at < 0) return null
  const held = bounded(duration, MIN_FREEZE_S, MAX_FREEZE_S, 0)
  // Zero-length would be an invisible stretch nobody could grab back.
  if (held <= 0) return null
  return { id, at, duration: held }
}

function parseClip(raw: unknown, knownVideoIds: Set<string>): ProjectClip | null {
  if (!isRecord(raw)) return null
  const { id, videoId, start, end, name, download, hidden, speed, freezes } = raw
  if (typeof id !== 'number' || typeof start !== 'number' || typeof end !== 'number') return null
  // A clip whose video is gone can't be drawn, cut or previewed — drop it
  // instead of carrying a dangling reference around the whole UI.
  if (typeof videoId !== 'string' || !knownVideoIds.has(videoId)) return null
  const dl =
    isRecord(download) && typeof download['url'] === 'string' && typeof download['filename'] === 'string'
      ? { url: download['url'], filename: download['filename'] }
      : null
  return {
    id,
    videoId,
    start,
    end,
    name: typeof name === 'string' ? name : '',
    download: dl,
    // The three below are absent on every project saved before speed,
    // freezes and hiding existed; their defaults are what those projects
    // already behaved like.
    hidden: hidden === true,
    speed: bounded(speed, MIN_SPEED, MAX_SPEED, 1),
    freezes: Array.isArray(freezes)
      ? freezes.map(parseFreeze).filter((f): f is FreezeFrame => f !== null)
      : [],
  }
}

function parseTransition(raw: unknown): TextTransition {
  if (!isRecord(raw)) return DEFAULT_TRANSITION
  const kind = raw['kind']
  const known = TRANSITION_OPTIONS.some((o) => o.value === kind)
  if (!known) return DEFAULT_TRANSITION
  return {
    kind: kind as TransitionKind,
    duration: bounded(raw['duration'], 0, 10, DEFAULT_TRANSITION.duration),
  }
}

function parseGrade(raw: unknown): ColorGrade | null {
  if (!isRecord(raw)) return null
  const { id, start, end } = raw
  if (typeof id !== 'string' || typeof start !== 'number' || typeof end !== 'number') return null
  if (end <= start) return null
  const L = GRADE_LIMITS
  return {
    id,
    start,
    end,
    brightness: bounded(raw['brightness'], L.brightness.min, L.brightness.max, DEFAULT_GRADE.brightness),
    contrast: bounded(raw['contrast'], L.contrast.min, L.contrast.max, DEFAULT_GRADE.contrast),
    saturation: bounded(raw['saturation'], L.saturation.min, L.saturation.max, DEFAULT_GRADE.saturation),
    hue: bounded(raw['hue'], L.hue.min, L.hue.max, DEFAULT_GRADE.hue),
  }
}

function parseCrop(raw: unknown): CropRect | null {
  if (!isRecord(raw)) return null
  const x = bounded(raw['x'], 0, 1, -1)
  const y = bounded(raw['y'], 0, 1, -1)
  const width = bounded(raw['width'], MIN_CROP, 1, -1)
  const height = bounded(raw['height'], MIN_CROP, 1, -1)
  if (x < 0 || y < 0 || width < 0 || height < 0) return null
  // A rectangle running off the frame would crop footage that doesn't
  // exist; ffmpeg fails outright on it, so it can't be let through.
  if (x + width > 1.0001 || y + height > 1.0001) return null
  return { x, y, width, height }
}

function parseText(raw: unknown): TextOverlay | null {
  if (!isRecord(raw)) return null
  const { id, text, start, end, x, y, size, fontFamily, color, background } = raw
  const { shadowSize, shadowColor } = raw
  if (typeof id !== 'string' || typeof start !== 'number' || typeof end !== 'number') return null
  if (end <= start) return null
  const frac = (v: unknown, fallback: number): number =>
    typeof v === 'number' && isFinite(v) && v >= 0 && v <= 1 ? v : fallback
  return {
    id,
    text: typeof text === 'string' ? text : DEFAULT_TEXT.text,
    start,
    end,
    // Out-of-range would put a caption off-screen with no way to grab it
    // back, so a bad value falls back to the default position.
    x: frac(x, DEFAULT_TEXT.x),
    y: frac(y, DEFAULT_TEXT.y),
    size: frac(size, DEFAULT_TEXT.size),
    fontFamily: typeof fontFamily === 'string' ? fontFamily : DEFAULT_TEXT.fontFamily,
    color: typeof color === 'string' ? color : DEFAULT_TEXT.color,
    background:
      background === 'none' || background === 'box' || background === 'shadow'
        ? background
        : DEFAULT_TEXT.background,
    // Absent on captions saved before the shadow was configurable.
    shadowSize:
      typeof shadowSize === 'number' && isFinite(shadowSize) && shadowSize >= 0 && shadowSize <= 1
        ? shadowSize
        : DEFAULT_TEXT.shadowSize,
    shadowColor: typeof shadowColor === 'string' ? shadowColor : DEFAULT_TEXT.shadowColor,
    // Absent before transitions existed; "none" is what those captions did.
    transitionIn: parseTransition(raw['transitionIn']),
    transitionOut: parseTransition(raw['transitionOut']),
  }
}

function parseProject(raw: unknown): ToolProject | null {
  if (!isRecord(raw)) return null
  const { id, name, videos, clips, texts, grades, crop, createdAt, updatedAt } = raw
  if (typeof id !== 'string' || !Array.isArray(videos)) return null
  const parsedVideos = videos.map(parseVideo).filter((v): v is ProjectVideo => v !== null)
  if (parsedVideos.length === 0) return null
  const videoIds = new Set(parsedVideos.map((v) => v.id))
  const parsedClips = Array.isArray(clips)
    ? clips.map((c) => parseClip(c, videoIds)).filter((c): c is ProjectClip => c !== null)
    : []
  const now = new Date().toISOString()
  return {
    id,
    name: typeof name === 'string' && name.trim() ? name : 'Projeto sem nome',
    videos: parsedVideos,
    clips: parsedClips,
    // Absent on projects saved before captions existed.
    texts: Array.isArray(texts)
      ? texts.map(parseText).filter((t): t is TextOverlay => t !== null)
      : [],
    grades: Array.isArray(grades)
      ? grades.map(parseGrade).filter((g): g is ColorGrade => g !== null)
      : [],
    crop: parseCrop(crop),
    createdAt: typeof createdAt === 'string' ? createdAt : now,
    updatedAt: typeof updatedAt === 'string' ? updatedAt : now,
  }
}

export function loadProjects(): ToolProject[] {
  const raw = readStorage(PROJECTS_KEY)
  if (!raw) return []
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.map(parseProject).filter((p): p is ToolProject => p !== null)
  } catch {
    return []
  }
}

export function saveProjects(projects: ToolProject[]): void {
  writeStorage(PROJECTS_KEY, JSON.stringify(projects))
}

export interface PlayerVolume {
  /** 0–1, the same scale as ``HTMLMediaElement.volume``. */
  volume: number
  muted: boolean
}

export const DEFAULT_VOLUME: PlayerVolume = { volume: 1, muted: false }

/** Saved player volume, or the default when nothing valid is stored. */
export function loadPlayerVolume(): PlayerVolume {
  const raw = readStorage(VOLUME_KEY)
  if (!raw) return DEFAULT_VOLUME
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!isRecord(parsed)) return DEFAULT_VOLUME
    const volume = parsed['volume']
    // Out-of-range would make the element throw on assignment, and NaN
    // would silently mute everything — both worth rejecting outright.
    if (typeof volume !== 'number' || !isFinite(volume) || volume < 0 || volume > 1) {
      return DEFAULT_VOLUME
    }
    return { volume, muted: parsed['muted'] === true }
  } catch {
    return DEFAULT_VOLUME
  }
}

export function savePlayerVolume(value: PlayerVolume): void {
  writeStorage(VOLUME_KEY, JSON.stringify(value))
}

/** Build a project around a single video — the shape every import takes. */
export function projectFromVideo(video: ProjectVideo, name?: string): ToolProject {
  const now = new Date().toISOString()
  return {
    id: newId('proj'),
    name: name?.trim() || stemOf(video.filename),
    videos: [video],
    clips: [],
    texts: [],
    grades: [],
    crop: null,
    createdAt: now,
    updatedAt: now,
  }
}

/**
 * One-shot adoption of the pre-project world.
 *
 * Before projects existed, clips were stored per video under
 * ``prism:clip-creator:clips:v1:{tempPath}`` and the home screen listed
 * raw files from disk. Now the home screen lists projects — so every
 * cached video becomes a project named after its file, inheriting the
 * clips (and export URLs) already saved for it.
 *
 * Returns the projects to prepend, or ``null`` when there's nothing to
 * do. The legacy keys are left untouched: they're a few bytes, and
 * keeping them means a bad migration is recoverable by hand.
 */
export function migrateLegacyImports(imports: ImportedItem[]): ToolProject[] | null {
  if (readStorage(MIGRATED_KEY)) return null
  // Mark first: a crash halfway through shouldn't create duplicates on
  // the next mount.
  writeStorage(MIGRATED_KEY, new Date().toISOString())
  if (imports.length === 0) return null

  const existing = loadProjects()
  const known = new Set(existing.flatMap((p) => p.videos.map((v) => v.tempPath)))

  const adopted: ToolProject[] = []
  for (const item of imports) {
    if (known.has(item.temp_path)) continue
    const video: ProjectVideo = {
      id: newId('vid'),
      tempPath: item.temp_path,
      videoUrl: item.video_url,
      proxyUrl: item.proxy_url,
      filename: item.filename,
      origin: item.kind === 'upload' ? 'upload' : 'youtube',
      duration: 0,
      width: null,
      height: null,
      fileSize: item.size_bytes,
    }
    const project = projectFromVideo(video)
    project.clips = loadStoredClips(item.temp_path).map((c) => ({
      id: c.id,
      videoId: video.id,
      start: c.start,
      end: c.end,
      name: '',
      download: c.download ?? null,
      hidden: false,
      speed: 1,
      freezes: [],
    }))
    project.createdAt = item.modified_at
    project.updatedAt = item.modified_at
    adopted.push(project)
  }

  if (adopted.length === 0) return null
  return adopted
}
